import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { portalAdminGetProviders, portalAdminListUsers, renameTransportProvider, mergeTransportProviders, getTransportProviderContacts, setTransportProviderContact } from "../../lib/api";
import TransportProviderAccessDialog from "./TransportProviderAccessDialog";

export function TransportProvidersTab() {
  const [providers, setProviders] = useState([]);
  const [users, setUsers] = useState([]);
  const [phones, setPhones] = useState({});   // name_lower -> phone (saved)
  const [phoneDraft, setPhoneDraft] = useState({}); // name_lower -> phone (editing)
  const [search, setSearch] = useState("");
  const [newProvider, setNewProvider] = useState("");
  const [activeProvider, setActiveProvider] = useState(null);
  const [renaming, setRenaming] = useState(null);   // { old, value }
  const [merging, setMerging] = useState(false);
  const [mergeSources, setMergeSources] = useState([]);
  const [mergeTarget, setMergeTarget] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [pRes, uRes, cRes] = await Promise.all([
        portalAdminGetProviders(),
        portalAdminListUsers({ role: "Transportation" }),
        getTransportProviderContacts(),
      ]);
      setProviders(pRes.data || []);
      setUsers(uRes.data || []);
      const map = {};
      (cRes.data || []).forEach((c) => { map[(c.name || "").toLowerCase()] = c.phone || ""; });
      setPhones(map);
      setPhoneDraft(map);
    } catch {
      toast.error("Failed to load transportation providers");
    }
  };
  useEffect(() => { load(); }, []);

  const savePhone = async (name) => {
    const key = name.toLowerCase();
    const draft = (phoneDraft[key] || "").trim();
    if (draft === (phones[key] || "")) return; // unchanged
    try {
      await setTransportProviderContact(name, draft);
      setPhones((p) => ({ ...p, [key]: draft }));
      toast.success("Contact number saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save number");
      setPhoneDraft((p) => ({ ...p, [key]: phones[key] || "" })); // revert
    }
  };

  const countFor = (name) =>
    users.filter((u) => (u.provider || "").toLowerCase() === name.toLowerCase()).length;

  const openNew = () => {
    const name = newProvider.trim();
    if (!name) { toast.error("Enter a provider name"); return; }
    setActiveProvider(name);
    setNewProvider("");
  };

  const submitRename = async () => {
    const nn = (renaming?.value || "").trim();
    if (!nn) { toast.error("Enter a new name"); return; }
    setBusy(true);
    try {
      const res = await renameTransportProvider(renaming.old, nn);
      toast.success(res.data.message || "Renamed");
      setRenaming(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally { setBusy(false); }
  };

  const toggleMergeSource = (name) => {
    setMergeSources((prev) => prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]);
  };

  const submitMerge = async () => {
    const sources = mergeSources.filter((s) => s.toLowerCase() !== mergeTarget.toLowerCase());
    if (!mergeTarget) { toast.error("Pick the provider to keep"); return; }
    if (sources.length === 0) { toast.error("Select at least one other provider to merge in"); return; }
    setBusy(true);
    try {
      const res = await mergeTransportProviders(sources, mergeTarget);
      toast.success(res.data.message || "Merged");
      setMerging(false);
      setMergeSources([]);
      setMergeTarget("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  const filtered = providers
    .filter((p) => p.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-4" data-testid="transport-providers-tab">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
        <strong>Transportation Providers</strong> are the bus/van companies that transport your students.
        Give a provider a limited portal login and they&apos;ll see only the students assigned to them (via each
        student&apos;s <strong>Transportation Provider</strong> field), view their addresses, set pickup/dropoff
        times, and get an email when one of their students is absent or leaving early.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search providers…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" data-testid="transport-providers-search" />
        <Button variant="outline" onClick={() => setMerging(true)} data-testid="transport-merge-btn">Merge Duplicates</Button>
        <div className="flex-1" />
        <Input placeholder="New provider name…" value={newProvider} onChange={(e) => setNewProvider(e.target.value)} className="w-56" data-testid="transport-new-provider-input" />
        <Button onClick={openNew} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="transport-add-provider-btn">+ Add Provider Login</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="transport-providers-table">
            <thead>
              <tr className="border-b bg-gray-50 text-left">
                <th className="p-3">Transportation Provider</th>
                <th className="p-3">Contact Number</th>
                <th className="p-3">Portal Logins</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">
                  No transportation providers yet. Set a student&apos;s Transportation Provider field, or add a provider login above.
                </td></tr>
              ) : filtered.map((p) => (
                <tr key={p} className="border-b hover:bg-gray-50" data-testid={`transport-provider-row-${p}`}>
                  <td className="p-3 font-medium">{p}</td>
                  <td className="p-3">
                    <Input
                      type="tel"
                      value={phoneDraft[p.toLowerCase()] ?? ""}
                      onChange={(e) => setPhoneDraft((d) => ({ ...d, [p.toLowerCase()]: e.target.value }))}
                      onBlur={() => savePhone(p)}
                      placeholder="Add number…"
                      className="h-8 w-40 text-sm"
                      data-testid={`transport-phone-${p}`}
                    />
                  </td>
                  <td className="p-3">
                    {countFor(p) > 0
                      ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">{countFor(p)} login{countFor(p) > 1 ? "s" : ""}</Badge>
                      : <span className="text-muted-foreground">None</span>}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-600 hover:bg-slate-100" onClick={() => setRenaming({ old: p, value: p })} data-testid={`transport-rename-${p}`}>Rename</Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-[#00305E] hover:bg-[#00305E]/10" onClick={() => setActiveProvider(p)} data-testid={`transport-access-${p}`}>Portal Access</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <TransportProviderAccessDialog
        provider={activeProvider}
        open={!!activeProvider}
        onOpenChange={(v) => { if (!v) { setActiveProvider(null); load(); } }}
      />

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={(v) => { if (!v) setRenaming(null); }}>
        <DialogContent data-testid="transport-rename-dialog">
          <DialogHeader>
            <DialogTitle>Rename Transportation Provider</DialogTitle>
            <DialogDescription>
              Renames &quot;{renaming?.old}&quot; everywhere it is used — on every student (primary &amp; second provider) and on any portal logins.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renaming?.value || ""}
            onChange={(e) => setRenaming((r) => ({ ...r, value: e.target.value }))}
            placeholder="New provider name"
            data-testid="transport-rename-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)} disabled={busy}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={submitRename} disabled={busy} data-testid="transport-rename-confirm">
              {busy ? "Renaming…" : "Rename"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge dialog */}
      <Dialog open={merging} onOpenChange={(v) => { if (!v) { setMerging(false); setMergeSources([]); setMergeTarget(""); } }}>
        <DialogContent data-testid="transport-merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge Duplicate Providers</DialogTitle>
            <DialogDescription>
              Combine duplicate provider names into one. Students &amp; portal logins on the selected providers are reassigned to the provider you keep.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Provider to keep</label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger data-testid="transport-merge-target"><SelectValue placeholder="Select the correct name" /></SelectTrigger>
                <SelectContent>
                  {[...providers].sort((a, b) => String(a).localeCompare(String(b))).map((p) => <SelectItem key={p} value={p} data-testid={`transport-merge-target-${p}`}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Merge these into it</label>
              <div className="mt-1 max-h-56 overflow-auto border rounded-lg divide-y">
                {providers.filter((p) => p !== mergeTarget).map((p) => (
                  <label key={p} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-gray-50">
                    <Checkbox checked={mergeSources.includes(p)} onCheckedChange={() => toggleMergeSource(p)} data-testid={`transport-merge-src-${p}`} />
                    {p}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMerging(false); setMergeSources([]); setMergeTarget(""); }} disabled={busy}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitMerge} disabled={busy} data-testid="transport-merge-confirm">
              {busy ? "Merging…" : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
